import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AccountService, { UserSession } from '../../../services/account.service';

const SessionsTab: React.FC = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const load = () => {
    setLoading(true);
    AccountService.listSessions()
      .then((res: any) => setSessions(res.data ?? []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const revokeOne = async (id: number) => {
    setRevoking(id);
    try {
      await AccountService.revokeSession(id);
      toast.success(t('account.sessions.revoked') as string);
      load();
    } catch {
      toast.error(t('account.sessions.revokeError') as string);
    } finally {
      setRevoking(null);
    }
  };

  const revokeAll = async () => {
    if (!window.confirm(t('account.sessions.revokeAllConfirm'))) return;
    setRevokingAll(true);
    try {
      await AccountService.revokeAllSessions();
      toast.success(t('account.sessions.revokedAll') as string);
      load();
    } catch {
      toast.error(t('account.sessions.revokeError') as string);
    } finally {
      setRevokingAll(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  const parseUA = (ua: string | null): string => {
    if (!ua) return t('account.sessions.unknownDevice');
    if (/iPhone|iPad/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Mac/.test(ua)) return 'macOS';
    if (/Linux/.test(ua)) return 'Linux';
    return ua.slice(0, 40);
  };

  const deviceIcon = (ua: string | null): string => {
    if (!ua) return 'ri-device-line';
    if (/Mobile|iPhone|Android/.test(ua)) return 'ri-smartphone-line';
    return 'ri-computer-line';
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div className="card" style={{ borderRadius: '0.75rem' }}>
        <div className="card-header border-bottom pb-3 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 fw-semibold">{t('account.sessions.title')}</h6>
          {sessions.length > 1 && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={revokeAll}
              disabled={revokingAll}
            >
              {revokingAll
                ? <span className="spinner-border spinner-border-sm me-2" />
                : null}
              {t('account.sessions.revokeAll')}
            </button>
          )}
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-4 text-muted small">{t('account.sessions.empty')}</div>
          ) : (
            <ul className="list-group list-group-flush">
              {sessions.map((s) => (
                <li key={s.id} className="list-group-item d-flex align-items-center gap-3 py-3 px-4">
                  <i className={`${deviceIcon(s.user_agent)} ri-24px text-muted`} />
                  <div className="flex-grow-1">
                    <div className="small fw-medium">{parseUA(s.user_agent)}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {s.ip_address && <span className="me-3">{s.ip_address}</span>}
                      {t('account.sessions.lastActive')}: {formatDate(s.last_active)}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => revokeOne(s.id)}
                    disabled={revoking === s.id}
                  >
                    {revoking === s.id
                      ? <span className="spinner-border spinner-border-sm" />
                      : <i className="ri-logout-box-r-line" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionsTab;
